import { useEffect, useState } from 'preact/hooks';
import { CHAT } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { applyFreshchatCustomer, loadFreshchat, openFreshchat } from './freshchat.js';
import { applyCustomer, loadLiveAgent, openLiveAgent } from './liveAgent.js';
import { chatFailed, chatReady, chatStarting } from './supportContact.js';

// LiveAgent's loader has no failure signal: a chat not ready by then counts as failed, so "contact us" is WhatsApp.
const LIVEAGENT_READY_TIMEOUT_MS = 10_000;

/**
 * 403 (#1317): the customer support chat on every page — Freshchat or LiveAgent, whichever the runtime config chooses
 * (none: nothing loads and "contact us" stays WhatsApp). Renders nothing itself; the provider draws its own button. The
 * customer is handed over once the chat is ready and again on every login, logout or profile change; while staff are
 * impersonating, nobody is (a chat must never be attributed to the customer). Replaces 378's `LiveAgentChat`.
 */
export function SupportChat({ aboveNav = false }: { aboveNav?: boolean }) {
  const { user, isImpersonating } = useAuth();
  const [ready, setReady] = useState(false);

  // While the bottom navigation shows, the chat button is lifted above it (`chat-above-nav` in index.css).
  useEffect(() => {
    document.documentElement.classList.toggle('chat-above-nav', aboveNav);
  }, [aboveNav]);

  useEffect(() => {
    if (!CHAT) return;
    chatStarting();
    if (CHAT.kind === 'freshchat') {
      loadFreshchat(
        CHAT.scriptUrl,
        () => {
          chatReady(() => openFreshchat());
          setReady(true);
        },
        chatFailed,
      );
      return;
    }
    let done = false;
    const timer = setTimeout(() => {
      if (!done) chatFailed();
    }, LIVEAGENT_READY_TIMEOUT_MS);
    loadLiveAgent(CHAT.config, () => {
      done = true;
      clearTimeout(timer);
      chatReady(() => openLiveAgent());
      setReady(true);
    });
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!ready || !CHAT) return;
    const customer = user && !isImpersonating ? user : null;
    if (CHAT.kind === 'freshchat') applyFreshchatCustomer(customer as never);
    else applyCustomer(customer);
  }, [ready, user, isImpersonating]);

  return null;
}
